import { Col, Row, Spinner2, Text } from 'components';
import { Api, POLL_ROLE, getPollRole, theme } from 'lib';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.SECONDARY : theme.GREY_30)};
  background: white;
  display: flex;
  align-items: center;
  padding: 0 14px;
`;

const StyledSelect = styled.select`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  font-family: CeraPro;
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  padding-right: 20px;
  color: ${(props) => (props.value ? theme.GREY_80 : theme.GREY_50)};

  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 10px;
  pointer-events: none;
  color: ${theme.GREY_60};
  display: flex;
  align-items: center;
`;

// "Home vs Away — MMM dd, yyyy HH:mm · League"
export const getUpcomingGameLabel = (game) => {
  if (!game) return '';
  const home = game.homeName || game.home?.name || 'TBD';
  const away = game.awayName || game.away?.name || 'TBD';
  // Kick-off time included: two fixtures of the same pairing on the same day were otherwise
  // indistinguishable here, which is precisely what a deleted-and-recreated match looks like.
  const dateStr = game.date ? format(new Date(game.date), 'MMM dd, yyyy HH:mm') : '';
  const league = game.league ? ` · ${game.league}` : '';
  return `${home} vs ${away}${dateStr ? ` — ${dateStr}` : ''}${league}`;
};

// Match picker for game-day polls.
//
// P4P admins use /admin/games/get (all games for the channel, past + future). Team
// (channel-owner) admins can't hit that endpoint (adminOnly → 403), so they use the
// owner-scoped /admin/polls/upcomingGames, which returns only their upcoming fixtures.
// `refreshKey`: change it to force a refetch — PollForm stays mounted for its close transition, so
// without it this list is fetched once per page load and can outlive the fixtures it describes. See
// the QuickQuiz GamePicker for the failure this caused.
const PollGamePicker = ({ teamId, value, onChange, hasError, disabled, refreshKey }) => {
  const user = useSelector((state) => state.user);
  const isP4P = getPollRole(user) === POLL_ROLE.P4P;

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!teamId || disabled) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let list = [];
      if (isP4P) {
        const res = await Api.post(
          '/admin/games/get',
          { teamId, sortBy: { date: -1 }, limit: 100 },
          null,
          { silent: true }
        );
        if (res?.games) list = res.games;
      } else {
        const res = await Api.get(
          `/admin/polls/upcomingGames?teamId=${teamId}&limit=50`,
          null,
          { silent: true }
        );
        if (res?.data) list = res.data;
      }
      if (!cancelled) {
        setGames(list);
        setLoading(false);
        setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId, disabled, isP4P, refreshKey]);

  // A selection no longer in the list is dropped rather than silently submitted: a <select> whose
  // value matches no option shows the placeholder, so the form read as empty while state still held
  // a dead id — and it passed validation, being truthy.
  const staleSelection = !disabled && loaded && !!value && !games.some((g) => g._id === value);

  useEffect(() => {
    if (staleSelection) onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staleSelection]);

  if (loading) {
    return (
      <Row center pad="8px 0">
        <Spinner2 />
        <Text marg="0 0 0 8px" col={theme.GREY_60}>
          Loading matches...
        </Text>
      </Row>
    );
  }

  if (!games.length && !disabled) {
    return (
      <Col
        wid="100%"
        pad="12px 14px"
        style={{ borderRadius: 8, border: `1px solid ${theme.GREY_30}`, background: theme.GREY_10 }}
      >
        <Text c2 col={theme.GREY_60}>
          {isP4P
            ? 'No matches found for this channel. A game-day poll needs a scheduled match.'
            : 'No upcoming matches for this channel. A game-day poll needs an upcoming match.'}
        </Text>
      </Col>
    );
  }

  return (
    <SelectContainer hasError={hasError} style={disabled ? { opacity: 0.5 } : undefined}>
      <StyledSelect
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
      >
        <option value="">Select a match...</option>
        {games.map((g) => (
          <option key={g._id} value={g._id}>
            {getUpcomingGameLabel(g)}
          </option>
        ))}
      </StyledSelect>
      <SelectArrow>
        <FiChevronDown size={14} />
      </SelectArrow>
    </SelectContainer>
  );
};

export default PollGamePicker;
